import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { memo, useCallback } from "react";

import { Field } from "../field";

import type { INewFastComplete } from "./types";
import { dropdownIcons } from "./icon";

import { AutoComplete } from "@/components/formControls";
import { useOptions } from "@/api/dropdowns";
import type { IOption } from "@/interfaces";
import type { ID } from "@/constants";
import { useReadOnly } from "@/hooks";
import { checkFormikEqual, clearFieldsDependencyValue } from "@utils";
import type { IDropdownType } from "@/api/dropdowns/config";
import { dropdownConfig, dropdownParamMapper } from "@/api/dropdowns/mapper";

export const FastComplete = memo(
  function FastComplete<TFormik, TTYpe extends IDropdownType>(
    props: INewFastComplete<TFormik, TTYpe>,
  ) {
    const {
      formik,
      name,
      type,
      placeholder,
      readOnly: propsReadOnly,
      dependencies = [],
      disabled,
      params,
      onInputChange,
      title: propsTitleKey,
      onChange,
      onClear,
    } = props;

    const readOnly = useReadOnly(propsReadOnly);

    const { t } = useTranslation("form");
    const paramMapper = dropdownParamMapper[type];

    const config = dropdownConfig[type];
    const options = config.hookOptions?.(params);

    const isHookIsEnabled = () => {
      if (readOnly) {
        return false;
      }
      if (options) {
        return options.enabled;
      }
    };

    const isInputIsDisabled = () => {
      if (disabled) {
        return true;
      }

      if (options === undefined) {
        return false;
      }
      return !options.enabled;
    };

    const {
      data,
      isFetching: isLoading,
      isError,
    } = useOptions({
      variables: paramMapper(params),
      enabled: isHookIsEnabled(),
      staleTime: ["userName", "visitHistories"]?.includes(type)
        ? undefined
        : Infinity,
      cacheTime: ["userName", "visitHistories"]?.includes(type)
        ? undefined
        : Infinity,
    });

    const dataList = data ? data.data : [];

    const { values, setFieldValue, errors, touched, handleBlur } = formik;

    const handleChange = useCallback(
      (value: IOption<ID>) => {
        setFieldValue(name, value);
        onChange?.(value);
        clearFieldsDependencyValue<TFormik>(
          setFieldValue,
          dependencies,
          "select",
        );
        onClear?.([name, ...dependencies]);
      },
      [dependencies, name, onChange, setFieldValue, onClear],
    );

    return (
      <Field
        name={name}
        title={propsTitleKey || type}
        formik={props.formik}
        icon={dropdownIcons[type]}
        isRequired={props.isRequired && !readOnly}
      >
        <AutoComplete
          onInputChange={onInputChange}
          isLoading={isLoading}
          isError={isError}
          placeholder={placeholder || t(propsTitleKey || type)}
          id={name.toString()}
          onChange={handleChange}
          onBlur={handleBlur}
          value={values[name] as IOption}
          options={dataList}
          validation={touched[name] && errors[name] ? "error" : undefined}
          readOnly={readOnly}
          disabled={isInputIsDisabled()}
        />
      </Field>
    );
  },
  (prevProps, nextProps) => checkFormikEqual(nextProps, prevProps),
) as unknown as <TFormik, TTYpe extends IDropdownType>(
  props: INewFastComplete<TFormik, TTYpe>,
) => JSX.Element;
